import { inject, Injectable } from '@angular/core';

import type {
	WebSocketCloseCode,
	WebSocketMessage
} from '@contexts/notifications/domain/models/websocket-message';
import { type EnvironmentRepository, hasValue, isNil } from '@contexts/shared/domain';

import { ENVIRONMENT_REPOSITORY } from '@dependencies/shared/domain/contracts';

type MessageCallback = (message: WebSocketMessage) => void;

type AuthenticationFailureHandler = () => Promise<boolean>;

export type SubscribeToWebSocketInput = {
	onMessage: (message: WebSocketMessage) => void;
};

@Injectable()
export class WebsocketService {
	private readonly environmentRepository: EnvironmentRepository = inject(ENVIRONMENT_REPOSITORY);

	private ws: WebSocket;
	private reconnectAttempts = 0;
	private reconnectTimeoutId: ReturnType<typeof setTimeout>;
	private messageCallbacks: MessageCallback[] = [];
	private shouldReconnect = true;
	private isReconnecting = false;
	private authenticationFailureHandler: AuthenticationFailureHandler | null = null;
	private hasRenewedSessionForHandshake = false;

	async connect(): Promise<void> {
		if (!this.environmentRepository.webSocketEnabled) {
			return;
		}

		if (hasValue(this.ws)) {
			// eslint-disable-next-line no-console
			console.log('WebSocket already connected');
			return;
		}

		this.shouldReconnect = true;
		this.reconnectAttempts = 0;
		this.hasRenewedSessionForHandshake = false;

		this.connectWebSocket();

		return;
	}

	disconnect(): void {
		if (!this.environmentRepository.webSocketEnabled) {
			return;
		}

		this.shouldReconnect = false;
		this.isReconnecting = false;
		this.clearReconnectTimeout();

		if (hasValue(this.ws)) {
			this.ws.close(1000, 'Normal closure');
			this.ws = null;
		}

		this.messageCallbacks = [];
		this.reconnectAttempts = 0;
	}

	onAuthenticationFailure(handler: AuthenticationFailureHandler): void {
		this.authenticationFailureHandler = handler;
	}

	onMessage(callback: MessageCallback): () => void {
		this.messageCallbacks.push(callback);
		return () => {
			const index = this.messageCallbacks.indexOf(callback);
			if (index > -1) {
				this.messageCallbacks.splice(index, 1);
			}
		};
	}

	sendMessage(message: Record<string, unknown>): void {
		if (hasValue(this.ws)) {
			try {
				this.ws.send(JSON.stringify(message));
			} catch (error) {
				console.error('Failed to send WebSocket message:', error);
			}
		} else {
			console.error('Cannot send message: WebSocket not connected');
		}
	}

	clearReconnectTimeout(): void {
		if (hasValue(this.reconnectTimeoutId)) {
			clearTimeout(this.reconnectTimeoutId);
			this.reconnectTimeoutId = null;
		}
	}

	getReconnectDelay(): number {
		console.error(
			`Rerty count: ${this.reconnectAttempts} -- Time to wait: ${Math.min(1000 * 2 ** (this.reconnectAttempts - 1), 60000)}`
		);
		return Math.min(1000 * 2 ** (this.reconnectAttempts - 1), 60000);
	}

	attemptReconnect(): void {
		if (!this.shouldReconnect) {
			return;
		}

		if (this.isReconnecting) {
			return;
		}

		if (this.reconnectAttempts >= this.environmentRepository.webSocketReconnectMaxAttempts) {
			console.error(
				`[WebSocket] Max reconnection attempts reached (${this.environmentRepository.webSocketReconnectMaxAttempts})`
			);
			return;
		}

		this.reconnectAttempts++;

		const delay = this.getReconnectDelay();
		// eslint-disable-next-line no-console
		console.log(
			`[WebSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.environmentRepository.webSocketReconnectMaxAttempts}) - backoff: 1s→2s→4s→8s→16s`
		);

		this.isReconnecting = true;
		this.reconnectTimeoutId = setTimeout(() => {
			this.isReconnecting = false;
			// eslint-disable-next-line no-console
			console.log(`[WebSocket] Attempting reconnection #${this.reconnectAttempts}...`);
			this.connectWebSocket();
		}, delay);
	}

	handleClose(event: CloseEvent): void {
		this.clearReconnectTimeout();
		this.ws = null;

		const closeCode = event.code as WebSocketCloseCode;

		console.error(`[WebSocket] Closed with code ${closeCode}: ${event.reason || 'no reason'}`);

		switch (closeCode) {
			case 1000:
				this.isReconnecting = false;
				this.shouldReconnect = false;
				// eslint-disable-next-line no-console
				console.log('[WebSocket] Normal closure - not reconnecting');
				break;

			case 1008:
				this.isReconnecting = false;
				this.shouldReconnect = false;
				console.error('[WebSocket] Authentication failed');
				this.reconnectWithRenewedSession();
				break;

			case 1006:
			case 1011:
				console.error('[WebSocket] Abnormal close - will reconnect');
				this.attemptReconnect();
				break;

			default:
				console.error('[WebSocket] Unexpected close code - will reconnect');
				this.attemptReconnect();
				break;
		}
	}

	private reconnectWithRenewedSession(): void {
		if (this.hasRenewedSessionForHandshake || isNil(this.authenticationFailureHandler)) {
			// eslint-disable-next-line no-console
			console.log('[WebSocket] Session cannot be renewed - not reconnecting');
			return;
		}

		this.hasRenewedSessionForHandshake = true;

		this.authenticationFailureHandler()
			.then((isSessionRenewed) => {
				if (!isSessionRenewed) {
					return;
				}

				// eslint-disable-next-line no-console
				console.log('[WebSocket] Session renewed - reconnecting');
				this.shouldReconnect = true;
				this.connectWebSocket();
			})
			.catch((error) => {
				console.error('[WebSocket] Failed to renew the session:', error);
			});
	}

	handleMessage(event: MessageEvent): void {
		try {
			const message = JSON.parse(event.data) as WebSocketMessage;
			this.reconnectAttempts = 0;

			// eslint-disable-next-line no-console
			console.log('[WebSocket] Recieved message: ', message);

			for (const callback of this.messageCallbacks) {
				callback(message);
			}
		} catch (error) {
			console.error('[WebSocket] Failed to parse WebSocket message:', error);
		}
	}

	connectWebSocket(): void {
		try {
			this.ws = new WebSocket(this.environmentRepository.webSocketBaseUrl);

			this.ws.onopen = () => {
				const wasReconnecting = this.reconnectAttempts > 0;
				this.isReconnecting = false;

				if (wasReconnecting) {
					// eslint-disable-next-line no-console
					console.log(
						`[WebSocket] Reconnected after ${this.reconnectAttempts} attempts - resetting counter`
					);
					this.reconnectAttempts = 0;
				} else {
					// eslint-disable-next-line no-console
					console.log('[WebSocket] Connected successfully');
				}
			};

			this.ws.onclose = (event) => this.handleClose(event);

			this.ws.onerror = (error) => {
				console.error('[WebSocket] Error:', error);
			};

			this.ws.onmessage = (event) => this.handleMessage(event);
		} catch (error) {
			this.ws = null;
			console.error('Failed to create WebSocket connection:', error);
			this.attemptReconnect();
		}
	}
}
